import { Box, Button, Skeleton, Typography } from "@mui/material";
import { Suspense, lazy } from "react";

import { DocumentPreview, type DocumentPreviewProps } from "./DocumentPreview";
import { isImage } from "./fileTypeUtils";
import { UnsupportedPreview } from "./UnsupportedPreview";

const LazySpreadsheetPreview = lazy(() =>
  import("./SpreadsheetPreview").then((m) => ({
    default: m.SpreadsheetPreview,
  }))
);

const LazyDocxPreview = lazy(() =>
  import("./DocxPreview").then((m) => ({ default: m.DocxPreview }))
);

const LazyImagePreview = lazy(() =>
  import("./ImagePreview").then((m) => ({ default: m.ImagePreview }))
);

interface FilePreviewProps extends Omit<DocumentPreviewProps, "fileUrl"> {
  fileUrl: string;
  fileExtension?: string;
}

function PreviewLoadingSkeleton() {
  return (
    <Skeleton
      variant="rectangular"
      animation="wave"
      height="100%"
      width="100%"
      sx={{ borderRadius: 0.5 }}
    />
  );
}

export function FilePreview({
  fileUrl,
  fileExtension,
  bottomLeftButtonProps,
  ...rest
}: FilePreviewProps) {
  const ext = fileExtension?.toLowerCase();

  switch (ext) {
    case "pdf":
      return (
        <DocumentPreview
          fileUrl={fileUrl}
          bottomLeftButtonProps={bottomLeftButtonProps}
          {...rest}
        />
      );

    case "xlsx":
    case "csv":
      return (
        <NonPdfPreviewWithButton bottomLeftButtonProps={bottomLeftButtonProps}>
          <LazySpreadsheetPreview fileUrl={fileUrl} fileExtension={ext} />
        </NonPdfPreviewWithButton>
      );

    case "docx":
      return (
        <NonPdfPreviewWithButton bottomLeftButtonProps={bottomLeftButtonProps}>
          <LazyDocxPreview fileUrl={fileUrl} />
        </NonPdfPreviewWithButton>
      );

    case "doc":
    case "xls":
      return (
        <NonPdfPreviewWithButton bottomLeftButtonProps={bottomLeftButtonProps}>
          <UnsupportedPreview fileUrl={fileUrl} fileExtension={ext} />
        </NonPdfPreviewWithButton>
      );

    default:
      if (ext && isImage(ext)) {
        return (
          <NonPdfPreviewWithButton bottomLeftButtonProps={bottomLeftButtonProps}>
            <LazyImagePreview fileUrl={fileUrl} />
          </NonPdfPreviewWithButton>
        );
      }
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No preview available
          </Typography>
        </Box>
      );
  }
}

function NonPdfPreviewWithButton({
  children,
  bottomLeftButtonProps,
}: {
  children: React.ReactNode;
  bottomLeftButtonProps?: DocumentPreviewProps["bottomLeftButtonProps"];
}) {
  return (
    <Suspense fallback={<PreviewLoadingSkeleton />}>
      <Box sx={{ position: "relative", height: "100%" }}>
        {children}
        {bottomLeftButtonProps && (
          <Button
            variant="contained"
            {...bottomLeftButtonProps}
            sx={{
              position: "absolute",
              bottom: 16,
              left: 16,
              zIndex: 1,
              ...bottomLeftButtonProps.sx,
            }}
          >
            {bottomLeftButtonProps.text}
          </Button>
        )}
      </Box>
    </Suspense>
  );
}
